<template src="./compilaAuditFSC.html"></template>

<script>
// Compilazione di un'edizione di audit FSC (iniziale o sorveglianza). Autosave: ogni modifica
// pianifica un salvataggio silenzioso dopo una breve pausa, cosi' non si perde mai nulla; il
// pulsante "Salva" forza subito lo stesso salvataggio. "Conferma" (solo admin) blocca in modo
// definitivo l'edizione (tranne la Chiusura NC, sempre compilabile).
import {
    get_audit_auditFSC,
    salva_audit_auditFSC,
    conferma_audit_auditFSC,
    salva_chiusuraNC_auditFSC,
    get_commesseSelezionabili_auditFSC,
    get_nonConformita_auditFSC,
    download_pdf_auditFSC,
} from "@/api/api.auditFSC.js";
import { carica_file_filtri } from "@/api/api.dati.js";
import CampoData from "@/components/cliente/CampoData.vue";

const STATI = ["C", "NC", "NA"];

export default {
    components: { CampoData },

    data: () => ({
        loading: false,
        pronto: false,
        salvando: false,
        ultimoSalvataggioOk: null,
        erroreSalvataggio: "",

        companyCode: "",
        numero: 0,

        dati: null,
        // Tabella NC/CAR: fuori da "dati" cosi' il suo aggiornamento non fa scattare l'autosave.
        nonConformita: [],
        commesseDisponibili: [],

        timerAutosave: null,

        dialogConferma: false,
    }),

    methods: {
        async caricaDati() {
            this.loading = true;
            this.pronto = false;
            const res = await get_audit_auditFSC(this.companyCode, this.numero);
            if (!res.data) {
                this.$toast.alert("Audit non trovato o non ancora programmato");
                this.$router.push({ path: "/audit-fsc" });
                return;
            }
            this.nonConformita = res.data.nonConformita || [];
            this.dati = res.data;
            this.completaModello();
            await this.caricaCommesseDisponibili();
            this.loading = false;
            // Evita che il popolamento iniziale del modello faccia scattare l'autosave.
            this.$nextTick(() => { this.pronto = true; });
        },

        // Garantisce che per ogni capitolo/requisito attivo esista una riga di risposta a cui
        // agganciare il v-model, anche per un audit appena creato (nessuna riga ancora salvata).
        completaModello() {
            for (const capitolo of this.dati.anagrafica) {
                if (!this.dati.risposteCapitolo.some((r) => r.capitoloId === capitolo.id)) {
                    this.dati.risposteCapitolo.push({ companyCode: this.companyCode, numero: this.numero, capitoloId: capitolo.id, stato: "" });
                }
                for (const requisito of capitolo.requisiti) {
                    if (!this.dati.risposteRequisiti.some((r) => r.requisitoId === requisito.id)) {
                        this.dati.risposteRequisiti.push({
                            companyCode: this.companyCode, numero: this.numero, requisitoId: requisito.id,
                            stato: "", risposta: "", sorveglianzaModificata: null,
                            ncTipo: "", ncDescrizione: "", azioneCorrettiva: "", evidenziata: false,
                        });
                    }
                }
            }
        },

        rispostaCapitolo(capitoloId) {
            return this.dati.risposteCapitolo.find((r) => r.capitoloId === capitoloId);
        },

        rispostaRequisito(requisitoId) {
            return this.dati.risposteRequisiti.find((r) => r.requisitoId === requisitoId);
        },

        // Numero del capitolo ricavato dai codici dei requisiti ("Req. 12.1/12.2" -> 12).
        numeroCapitolo(capitolo) {
            for (const r of capitolo.requisiti) {
                const m = /^Req\.?\s*(\d+)/i.exec(r.codice || "");
                if (m) return m[1];
            }
            return "";
        },

        // Avanzamento di un capitolo: requisiti gia' valutati / totale. Un capitolo NA e' completo; in sorveglianza
        // (con audit precedente) serve anche aver risposto a "ci sono state modifiche?".
        avanzamentoCapitolo(capitolo) {
            const totale = capitolo.requisiti.length;
            const rispostaCap = this.rispostaCapitolo(capitolo.id);
            if (rispostaCap && rispostaCap.stato === "NA") return { fatte: totale, totale, completo: true };

            const confermaModifiche = this.numero > 0 && this.dati.datiGenerali.haRispostePrecedenti;
            const fatte = capitolo.requisiti.filter((req) => {
                const r = this.rispostaRequisito(req.id);
                return r && r.stato && (!confermaModifiche || r.sorveglianzaModificata !== null);
            }).length;
            return { fatte, totale, completo: totale > 0 && fatte === totale };
        },

        capitoloVisibileRequisiti(capitoloId) {
            const risposta = this.rispostaCapitolo(capitoloId);
            return !risposta || risposta.stato !== "NA";
        },

        async caricaCommesseDisponibili() {
            const res = await get_commesseSelezionabili_auditFSC(this.companyCode);
            this.commesseDisponibili = (res.data || []).map((c) => ({
                id: c.id,
                etichetta: c.riferimento + (c.clienteUscita ? " - " + c.clienteUscita : ""),
            }));
        },

        pianificaAutosave() {
            if (!this.pronto || this.soloLettura) return;
            clearTimeout(this.timerAutosave);
            this.timerAutosave = setTimeout(() => this.salva(true), 900);
        },

        async salva(silenzioso) {
            if (this.soloLettura) return;
            clearTimeout(this.timerAutosave);
            this.salvando = true;
            const res = await salva_audit_auditFSC(this.dati, silenzioso);
            this.salvando = false;

            if (res.data === "OK") {
                this.ultimoSalvataggioOk = true;
                this.erroreSalvataggio = "";
                if (!silenzioso) this.$toast.info("Salvato");
                await this.aggiornaNonConformita();
            } else {
                this.ultimoSalvataggioOk = false;
                this.erroreSalvataggio = res.data;
                this.$toast.alert(res.data);
                if (String(res.data).toLowerCase().includes("confermat")) await this.caricaDati();
            }
        },

        // Le NC spuntate compaiono subito nella tabella in fondo (il server le sincronizza a ogni salvataggio).
        // Si tiene la Chiusura NC digitata localmente, non ancora salvata, per non sovrascriverla.
        async aggiornaNonConformita() {
            const res = await get_nonConformita_auditFSC(this.companyCode);
            const locali = new Map(this.nonConformita.map((r) => [r.id, r.chiusuraNC]));
            this.nonConformita = (res.data || []).map((r) => (locali.has(r.id) ? { ...r, chiusuraNC: locali.get(r.id) } : r));
        },

        async scaricaPdf() {
            const res = await download_pdf_auditFSC(this.companyCode, this.numero);
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Audit_FSC_${this.dati.datiGenerali.tipoAudit.replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        },

        aggiungiCommessa() {
            if (this.dati.commesse.length >= 3) return;
            this.dati.commesse.push({
                ordine: this.dati.commesse.length + 1, cliente: "", riferimentoCommessaId: null,
                riferimentoTesto: "", sistemaRegistro: "Gestionale", gruppiProdotto: [],
            });
        },

        rimuoviCommessa(index) {
            this.dati.commesse.splice(index, 1);
            this.dati.commesse.forEach((c, i) => { c.ordine = i + 1; });
            this.pianificaAutosave();
        },

        aggiungiGruppoProdotto(commessa) {
            commessa.gruppiProdotto.push({
                ordine: commessa.gruppiProdotto.length + 1, stato: "", input: "", output: "",
                dichiarazioneFSCCorretta: null, codiceCoCCorretto: null, gruppoProdottoSelezionatoCorretto: null,
                terzisti: "", ncTipo: "", ncDescrizione: "", azioneCorrettiva: "",
            });
        },

        rimuoviGruppoProdotto(commessa, index) {
            commessa.gruppiProdotto.splice(index, 1);
            commessa.gruppiProdotto.forEach((g, i) => { g.ordine = i + 1; });
            this.pianificaAutosave();
        },

        async caricaFirma(tipo, file) {
            if (!file) return;
            const nomeFile = tipo + "." + (file.name.split(".").pop() || "png");
            const path = `UserFile/${this.companyCode}/AuditFSC/${this.numero}/${nomeFile}`;

            const formData = new FormData();
            formData.append("file", file);
            const res = await carica_file_filtri(formData, "?path=" + encodeURIComponent(path));

            if (res.data.message === "OK") {
                if (tipo === "firma-auditor") this.dati.header.firmaAuditorPath = path;
                else this.dati.header.firmaAziendaPath = path;
                this.pianificaAutosave();
            } else {
                this.$toast.alert("Errore nel caricamento della firma: " + res.data.message);
            }
        },

        async confermaAudit() {
            this.dialogConferma = false;
            const res = await conferma_audit_auditFSC(this.companyCode, this.numero);
            if (typeof res.data === "string" && res.data.startsWith("OK")) {
                // "OK - ..." = confermato, ma PDF/mail con avvisi: si mostrano.
                this.$toast.info(res.data === "OK" ? "Audit confermato" : "Audit confermato (" + res.data.substring(5) + ")");
                await this.caricaDati();
            } else {
                this.$toast.alert(res.data);
            }
        },

        async salvaChiusura(riga) {
            const res = await salva_chiusuraNC_auditFSC({ Id: riga.id, ChiusuraNC: riga.chiusuraNC });
            if (res.data === "OK") this.$toast.info("Chiusura NC salvata");
            else this.$toast.alert(res.data);
        },

        indietro() {
            this.$router.push({ path: "/audit-fsc" });
        },
    },

    computed: {
        getRole() {
            return this.$store.getters["user/getRole"];
        },

        soloLettura() {
            return !this.dati || this.dati.header.confermato;
        },

        statiPossibili() {
            return STATI;
        },
    },

    watch: {
        dati: {
            handler() { this.pianificaAutosave(); },
            deep: true,
        },
    },

    created() {
        if (this.getRole !== "admin" && this.getRole !== "auditor") {
            this.$router.push({ path: "/" });
            return;
        }
        this.companyCode = this.$route.params.companyCode;
        this.numero = Number(this.$route.params.numero);
        this.caricaDati();
    },

    beforeRouteLeave(to, from, next) {
        clearTimeout(this.timerAutosave);
        if (this.pronto && !this.soloLettura) this.salva(true);
        next();
    },
};
</script>
